// covered-by: packages/schemas/src/__tests__/askPuntt-schema.spec.ts
// covered-by: packages/schemas/src/christo/__tests__/chatAtoms.test.ts
import { z } from "zod";

import { askPunttOpenItemContextsSchema } from "./askPunttOpenItems";
import {
  askPunttListSortBySchema,
  askPunttOrgRegionsQuerySchema,
  askPunttOrgRegionsSchema,
  askPunttOrgTagsQuerySchema,
  askPunttOrgTagsSchema,
} from "./askPunttOrgLabels";
import { askPunttChatAtomSchema } from "./chatAtoms";
import { askPunttVersionComparisonSchema } from "./versionComparison";

import { demoBaselineSchema } from "../commons/enterprise";
import { ticketMessageCitationSchema } from "../commons/ticket";

export type { AskPunttListSortBy } from "./askPunttOrgLabels";

/** Per-chat ROI facts for the meeting_roi skill (not stored on Enterprise). */
export const askPunttRoiSessionSchema = demoBaselineSchema;
export type AskPunttRoiSession = z.infer<typeof askPunttRoiSessionSchema>;

/**
 * What warhol can open, by lowercase extension (no dot). The single source of
 * truth for the warhol attachment allowlist: christo gates its 400 on this set
 * (`warholChat.ts::WARHOL_DOCUMENT_EXTENSIONS`) and carrie derives the composer
 * picker's warhol list from it (`useFileDrop.ts::WARHOL_ACCEPTED_FILE_TYPES`),
 * so the picker cannot offer a shape the send would refuse. Kept here — the
 * shared FE/backend contract — so one edit moves both sides. Mirrors
 * `contracts/chat.py` on warhol's side (warhol spec 0012): PDFs and images it
 * renders; office shapes it reads; legacy office shapes it serves as the PDF
 * samba converts. No video, zip or rtf — warhol has no reader for those.
 */
export const WARHOL_DOCUMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  "pdf",
  "ai",
  "png",
  "jpg",
  "jpeg",
  "tif",
  "tiff",
  "bmp",
  "gif",
  "webp",
  "docx",
  "pptx",
  "xlsx",
  "xlsm",
  "csv",
  "tsv",
  "txt",
  "doc",
  "xls",
  "ppt",
  "odt",
  "odp",
  "ods",
]);

// Workflow types — built-in skills + custom_<uuid> for enterprise custom workflows
export const askPunttWorkflowTypeSchema = z.union([
  z.enum([
    "compliance_review",
    "artwork_review",
    "compare",
    "review",
    "brief_creation",
    "claim_generation",
    "general",
    // @deprecated Legacy "chat as a project" workflow. Superseded by the
    // first-class AskPunttProject entity (a chat now links to a project via
    // chat.projectId + projectChatKind, with workflowType "general"). Retained
    // because the live carrie FE still filters on it; removal ships with the
    // FE migration.
    "project",
    // Greeting-only home page card. Opening it shows the "Connectors" canned
    // greeting; it does not have a dedicated review workflow of its own.
    "connectors",
  ]),
  z.string().startsWith("custom_"),
]);
export type AskPunttWorkflowType = z.infer<typeof askPunttWorkflowTypeSchema>;

// Source result in search progress
export const askPunttSourceResultSchema = z.object({
  title: z.string(),
  url: z.string().optional(),
  type: z.string().optional(), // e.g. "document", "ticket", "regulation"
  // Optional opaque ID — used by rembrandt's "Create Asset" search-progress
  // entries to thread ticket IDs through to the chat (so per-ticket logic
  // like multi-asset checklist labels can resolve titles by id).
  id: z.string().optional(),
});
export type AskPunttSourceResult = z.infer<typeof askPunttSourceResultSchema>;

// Search progress for a single source
export const askPunttSearchProgressSchema = z.object({
  source: z.string(), // e.g. "Puntt Regulatory Database", "Danone's Claims Data", "Wrike"
  status: z.enum(["searching", "done", "error"]),
  resultCount: z.number().optional(),
  results: z.array(askPunttSourceResultSchema).optional(),
});
export type AskPunttSearchProgress = z.infer<
  typeof askPunttSearchProgressSchema
>;

// Alternative claim suggestion
export const askPunttAlternativeSchema = z.object({
  riskLevel: z.enum(["low", "medium", "high"]),
  claim: z.string(),
  why: z.string().optional(),
  precedent: z.string().optional(),
  status: z.string().optional(),
  approvalPath: z.string().optional(),
  tradeOff: z.string().optional(),
});
export type AskPunttAlternative = z.infer<typeof askPunttAlternativeSchema>;

// Source citation
export const askPunttSourceCitationSchema = z.object({
  label: z.string(), // e.g. "Puntt Regulatory Database", "EUR Lex", "ASA"
  url: z.string().optional(),
  type: z.enum(["regulatory", "internal", "external"]).optional(),
});
export type AskPunttSourceCitation = z.infer<
  typeof askPunttSourceCitationSchema
>;

// Structured content for rich AI responses
export const askPunttStructuredContentSchema = z.object({
  riskLevel: z.enum(["low", "medium", "high"]).optional(),
  confidence: z.enum(["low", "medium", "high"]).optional(),
  shortAnswer: z.string().optional(),
  regulatoryAnalysis: z.string().optional(),
  alternatives: z.array(askPunttAlternativeSchema).optional(),
  sources: z.array(askPunttSourceCitationSchema).optional(),
  timeSaved: z
    .object({
      summary: z.string(), // e.g. "2-3 Weeks"
      breakdown: z.array(
        z.object({
          week: z.string(),
          description: z.string(),
        }),
      ),
    })
    .optional(),
});
export type AskPunttStructuredContent = z.infer<
  typeof askPunttStructuredContentSchema
>;

// Attachment (S3 reference)
export const askPunttAttachmentSchema = z.object({
  key: z.string(),
  source: z.string().url(),
  type: z.enum(["image", "video", "file"]),
  name: z.string().optional(),
});
export type AskPunttAttachment = z.infer<typeof askPunttAttachmentSchema>;

/**
 * Server-side bounds on user-controlled quoted-context input. `quotedContexts`
 * is persisted verbatim into the chat's `messages[]` subdocument array and
 * interpolated into the model prompt, so it must be capped in the shared schema
 * — the client trim is advisory (any authenticated caller can POST directly).
 * Kept here, alongside the other bounded fields in this file, so the carrie
 * composer (`ASK_V2_QUOTED_CONTEXT_MAX_CHARS`) and the server cannot drift.
 */
export const ASK_PUNTT_QUOTED_CONTEXT_MAX_CHARS = 200;
export const ASK_PUNTT_QUOTED_CONTEXT_MAX_COUNT = 10;

/** Highlighted snippet attached as composer context (Ask V2 warhol chats). */
export const askPunttQuotedContextSchema = z.object({
  id: z.string(),
  text: z.string().max(ASK_PUNTT_QUOTED_CONTEXT_MAX_CHARS),
  sourceMessageId: z.string().optional(),
});
export type AskPunttQuotedContext = z.infer<typeof askPunttQuotedContextSchema>;

/** Bounded array of composer quoted contexts, shared by every request schema. */
export const askPunttQuotedContextsSchema = z
  .array(askPunttQuotedContextSchema)
  .max(ASK_PUNTT_QUOTED_CONTEXT_MAX_COUNT);

export const ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS = 200;
export const ASK_PUNTT_COMMENT_CONTEXT_MAX_COUNT = 1;

/** Compact review-finding reference attached as composer context (Ask V2). */
export const askPunttCommentContextSchema = z.object({
  id: z.string(),
  commentId: z.string(),
  reviewId: z.string(),
  // `min(1)`, not `positive()`: the OpenAPI generator renders positive() as a
  // boolean exclusiveMinimum, which the draft-07 validator in jest.setup rejects.
  number: z.number().int().min(1),
  title: z.string().max(ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS),
  /** Optional normalized tag id for chip display (e.g. nutrition). */
  tag: z.string().optional(),
});
export type AskPunttCommentContext = z.infer<
  typeof askPunttCommentContextSchema
>;

export const askPunttCommentContextsSchema = z
  .array(askPunttCommentContextSchema)
  .max(ASK_PUNTT_COMMENT_CONTEXT_MAX_COUNT);

/**
 * One reviewer's verdict in a `run_subagents` fan-out.
 *
 * Named and exported so the shape has ONE owner: christo projects a warhol
 * tool return into it and carrie reads it back off the step, and the two
 * hand-written copies drifting apart is the very failure this field exists to
 * stop -- a verdict the producer sends and a consumer cannot see is a reviewer
 * that renders as finished when it failed.
 */
export const askPunttSubagentOutcomeSchema = z.object({
  name: z.string(),
  status: z.string(),
  error: z.string().optional(),
});
export type AskPunttSubagentOutcome = z.infer<
  typeof askPunttSubagentOutcomeSchema
>;

// Agent step (thinking, tool call, tool result) for real-time activity log
export const askPunttAgentStepSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("thinking"),
    content: z.string(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("tool_call"),
    stepId: z.string().optional(),
    tool: z.string(),
    displayName: z.string(),
    statusName: z.string().optional(),
    args: z.record(z.unknown()).optional(),
    description: z.string().optional(),
    toolStartedAt: z.string().optional(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("tool_result"),
    stepId: z.string().optional(),
    tool: z.string(),
    toolName: z.string().optional(),
    displayName: z.string(),
    statusName: z.string().optional(),
    summary: z.string(),
    resultCount: z.number().optional(),
    /**
     * Per-report outcomes of a `run_subagents` call, lifted out before christo
     * caps `summary` to 500 chars. The cap truncates the reports into JSON that
     * cannot be parsed, so without this every reviewer -- failed ones included
     * -- rendered as the same finished row.
     */
    subagentOutcomes: z.array(askPunttSubagentOutcomeSchema).optional(),
    toolStartedAt: z.string().optional(),
    toolCompletedAt: z.string().optional(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("text"),
    content: z.string(),
    roundIndex: z.number(),
    timestamp: z.string(),
  }),
]);
export type AskPunttAgentStep = z.infer<typeof askPunttAgentStepSchema>;

// Content block types for block-based message rendering
export const askPunttContentBlockSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    content: z.string(),
  }),
  z.object({
    type: z.literal("tool_activity"),
    stepRange: z.tuple([z.number(), z.number()]),
  }),
]);
export type AskPunttContentBlock = z.infer<typeof askPunttContentBlockSchema>;

// Canvas block types for rich structured output
export const askPunttCanvasBlockSchema: z.ZodType = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("heading"),
      level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
      text: z.string(),
    }),
    z.object({ type: z.literal("paragraph"), text: z.string() }),
    z.object({
      type: z.literal("badge"),
      label: z.string(),
      color: z.enum(["green", "amber", "red", "blue", "gray"]),
      size: z.enum(["sm", "md", "lg"]).optional(),
    }),
    z.object({
      type: z.literal("card"),
      title: z.string(),
      subtitle: z.string().optional(),
      badge: z.record(z.unknown()).optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("grid"),
      columns: z.number(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("table"),
      headers: z.array(z.string()),
      rows: z.array(z.array(z.string())),
      highlightRules: z.record(z.string()).optional(),
    }),
    z.object({
      type: z.literal("list"),
      ordered: z.boolean(),
      items: z.array(z.record(z.unknown())),
    }),
    z.object({ type: z.literal("divider") }),
    z.object({
      type: z.literal("metric"),
      label: z.string(),
      value: z.string(),
      trend: z
        .enum([
          "up-positive",
          "up-negative",
          "down-positive",
          "down-negative",
          "neutral",
          "up",
          "down",
        ])
        .optional(),
    }),
    z.object({
      type: z.literal("expandable"),
      title: z.string(),
      summary: z.string().optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("section"),
      id: z.string(),
      title: z.string(),
      editable: z.boolean().optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("source_link"),
      title: z.string(),
      url: z.string(),
      linkType: z.string().optional(),
    }),
    z.object({
      type: z.literal("chart"),
      chartType: z.enum(["bar", "pie", "line"]),
      data: z.record(z.unknown()),
    }),
    z.object({
      type: z.literal("callout"),
      variant: z.enum(["info", "warning", "success", "error"]),
      text: z.string(),
    }),
    z.object({
      type: z.literal("file_preview"),
      url: z.string(),
      fileName: z.string(),
      fileType: z.enum([
        "pdf",
        "ai",
        "xlsx",
        "xls",
        "docx",
        "doc",
        "pptx",
        "ppt",
      ]),
      pageCount: z.number().optional(),
    }),
    // why these two were missing: the renderer draws them
    // (VideoPreviewBlock, ChecklistItemBlock) but the wire schema never knew
    // them, so the two lists disagreed. Nothing broke because this schema is
    // not used to validate — `askPunttCanvasContentSchema.blocks` is
    // `z.array(z.record(z.unknown()))`. Aligned so the schema is a superset of
    // what renders, which is the precondition for ever gating on it.
    z.object({
      type: z.literal("video_preview"),
      url: z.string(),
      fileName: z.string(),
    }),
    z.object({
      type: z.literal("checklist_item"),
      criterion: z.string(),
      status: z.enum(["pass", "fail", "partial", "pending"]),
      finding: z.string(),
    }),
  ]),
);
export type AskPunttCanvasBlock = z.infer<typeof askPunttCanvasBlockSchema>;

// Canvas content wrapping blocks
export const askPunttCanvasContentSchema = z.object({
  outputType: z.enum([
    "risk_assessment",
    "brief_document",
    "comparison",
    "report",
    "visualization",
    "custom",
  ]),
  blocks: z.array(z.record(z.unknown())),
  rawHtml: z.string().nullable().optional(),
  exportFormats: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type AskPunttCanvasContent = z.infer<typeof askPunttCanvasContentSchema>;

// Structured question option (for multi-question forms)
export const askPunttQuestionOptionSchema = z.object({
  label: z.string(),
  description: z.string(),
  value: z.string().optional(),
  isUpload: z.boolean().optional(),
});
export type AskPunttQuestionOption = z.infer<
  typeof askPunttQuestionOptionSchema
>;

// Structured question (used by AskUserQuestion tool)
export const askPunttUserQuestionSchema = z.object({
  /** Stable id for cancel/edit without sending a new turn. */
  id: z.string().optional(),
  question: z.string(),
  header: z.string(),
  options: z.array(askPunttQuestionOptionSchema),
  multiSelect: z.boolean().optional(),
  allowFiles: z.boolean().optional(),
  status: z.enum(["open", "cancelled", "answered"]).optional(),
});
export type AskPunttUserQuestion = z.infer<typeof askPunttUserQuestionSchema>;

/** Structured private/public offer — never free-text prose. */
export const askPunttScopeOfferSchema = z.object({
  reason: z.string(),
  default: z.enum(["personal", "shared"]),
  privatePreview: z.string(),
  sharedPreview: z.string(),
  reviewId: z.string().optional(),
});
export type AskPunttScopeOffer = z.infer<typeof askPunttScopeOfferSchema>;

// Project stage status
export const askPunttProjectStageSchema = z.object({
  name: z.string(),
  status: z.enum(["pending", "active", "done", "blocked", "needs_review"]),
  color: z.enum(["green", "amber", "red", "gray"]),
});
export type AskPunttProjectStage = z.infer<typeof askPunttProjectStageSchema>;

/**
 * A stage of the live artwork-review progress bar. Separate from
 * askPunttProjectStageSchema, which it extends: that one is also the shape of
 * `projectData.stages` (business stages — Brand, Legal…), where a per-stage
 * review `detail` would be meaningless.
 */
export const reviewStageSchema = askPunttProjectStageSchema.extend({
  /** Short factual lines about what the stage did ("2 documents",
   * "8 findings found"). Absent when the stage has no numbers to report. */
  detail: z.array(z.string()).optional(),
});
export type ReviewStage = z.infer<typeof reviewStageSchema>;

/**
 * One producer of findings the review dispatched: a scout the primary agent
 * fans out to during the "Review" stage, or a deterministic pass the pipeline
 * runs alongside them. Both share this shape — each produces the finding rows
 * that follow it in the stream — so the chat renders them as one list.
 *
 * "skipped" is a pass that could not run at all (an outlined pack with no
 * readable text): reporting it is what keeps "we could not read this" distinct
 * from "this check is switched off", which look identical when a row is simply
 * absent.
 */
export const reviewScoutRowSchema = z.object({
  kind: z.literal("scout"),
  id: z.string(),
  label: z.string(),
  status: z.enum([
    "pending",
    "running",
    "done",
    "failed",
    "timed_out",
    "skipped",
  ]),
  draftCount: z.number(),
});
export type ReviewScoutRow = z.infer<typeof reviewScoutRowSchema>;

/**
 * One candidate finding, with the lifecycle the pipeline drives it through:
 * a scout drafts it -> the primary emits it as a finding intent -> an evidence
 * agent verifies it. `dismissed` = the primary triaged the draft away without
 * emitting it; `unresolved` = verification timed out, so it was never judged.
 */
export const reviewFindingRowSchema = z.object({
  kind: z.literal("finding"),
  id: z.string(),
  title: z.string(),
  state: z.enum([
    "draft",
    "emitted",
    "verifying",
    "accepted",
    "rejected",
    "dismissed",
    "unresolved",
    // Verified, then folded into another finding by the dedup pre-pass. The row
    // stays in the stream (with `note` naming its representative) so a merged
    // finding does not simply vanish from the progress panel.
    "merged",
  ]),
  description: z.string().optional(),
  area: z.string().optional(),
  page: z.number().optional(),
  confidence: z.number().optional(),
  note: z.string().optional(),
});
export type ReviewFindingRow = z.infer<typeof reviewFindingRowSchema>;

/**
 * The live review stream: a flat, ordered list of scout and finding rows the
 * backend rebuilds on every progress event. The wire format stays a flat
 * ordered list (producer, then its findings, then the next producer). The UI
 * nests that list by order into checks inside the Review stage.
 */
export const reviewStreamRowSchema = z.discriminatedUnion("kind", [
  reviewScoutRowSchema,
  reviewFindingRowSchema,
]);
export type ReviewStreamRow = z.infer<typeof reviewStreamRowSchema>;

// Project reviewer routing
export const askPunttProjectReviewerSchema = z.object({
  name: z.string(),
  initials: z.string(),
  role: z.string(),
  mode: z.enum(["Parallel", "After"]),
  status: z.enum(["active", "pending", "done", "needs_review"]),
});
export type AskPunttProjectReviewer = z.infer<
  typeof askPunttProjectReviewerSchema
>;

// Project forecast data
export const askPunttProjectForecastSchema = z.object({
  cycleTimeDays: z.number(),
  vsTargetDays: z.number(),
  confidence: z.number().min(0).max(100),
});
export type AskPunttProjectForecast = z.infer<
  typeof askPunttProjectForecastSchema
>;

export const askPunttProjectPlanStepSchema = z.object({
  title: z.string(),
  description: z.string(),
  status: z.enum(["pending", "active", "done"]),
});
export type AskPunttProjectPlanStep = z.infer<
  typeof askPunttProjectPlanStepSchema
>;

// Per-ticket finding summary (populated after review completion)
export const askPunttTicketFindingSchema = z.object({
  ticketId: z.string(),
  ticketTitle: z.string(),
  commentCount: z.number(),
  topFindings: z.array(z.string()),
  severity: z.enum(["clean", "minor", "major"]),
  /**
   * Per-asset risk rollup, surfaced as a chip on each asset card in
   * `ProjectStatusPane`. Distinct from `severity`: severity is the
   * highest finding severity on the ticket, riskScore is the
   * three-bucket label the demo wants Bill (and any other buyer) to
   * see ("these three came back low / medium / high").
   *
   * Phase 1 of the 3-tier asset flow plan. Optional because rembrandt-
   * produced project data does not (yet) populate it.
   */
  riskScore: z.enum(["low", "medium", "high"]).optional(),
});
export type AskPunttTicketFinding = z.infer<typeof askPunttTicketFindingSchema>;

// Compliance verdict tallies — bucket counts for claim-level review outcomes.
// Bucket names mirror ComplianceVerdict in commons/ticket.ts.
// `non_compliant` is the internal storage key (UI renders "Non-compliant").
export const askPunttComplianceTallySchema = z.object({
  approved: z.number().default(0),
  derivative: z.number().default(0),
  non_compliant: z.number().default(0),
  unsure: z.number().default(0),
  total: z.number().default(0),
});
export type AskPunttComplianceTally = z.infer<
  typeof askPunttComplianceTallySchema
>;

export const askPunttTicketComplianceSchema =
  askPunttComplianceTallySchema.extend({
    ticketId: z.string(),
  });
export type AskPunttTicketCompliance = z.infer<
  typeof askPunttTicketComplianceSchema
>;

export const askPunttComplianceSummarySchema =
  askPunttComplianceTallySchema.extend({
    ticketsWithCompliance: z.number(),
    ticketsReviewed: z.number(),
    ticketsExpected: z.number(),
    isComplete: z.boolean(),
    generatedAt: z.string(),
  });
export type AskPunttComplianceSummary = z.infer<
  typeof askPunttComplianceSummarySchema
>;

// Full project data (lives on chat + message level)
/**
 * @deprecated Legacy chat-as-project payload. Superseded by the first-class
 * AskPunttProject entity (packages/schemas/src/christo/askPunttProject.ts).
 * Retained because the live carrie FE still renders it; removal ships with the
 * FE migration.
 */
export const askPunttProjectDataSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  markets: z.array(z.string()),
  projectType: z.string(),
  stages: z.array(askPunttProjectStageSchema),
  routing: z.array(askPunttProjectReviewerSchema),
  forecast: askPunttProjectForecastSchema,
  aiRecommendation: z.string().optional(),
  targetDeadline: z.string().optional(),
  completedAt: z.string().optional(),
  confidence: z.number().min(0).max(100).optional(),
  intent: z.string().optional(),
  planSteps: z.array(askPunttProjectPlanStepSchema).optional(),
  reviewScope: z.enum(["full", "quick"]).optional(),
  findingsSummary: z.string().optional(),
  findingsSummaryGeneratedAt: z.string().optional(),
  ticketFindings: z.array(askPunttTicketFindingSchema).optional(),
  ticketIds: z.array(z.string()).optional(),
  ticketCompliance: z.array(askPunttTicketComplianceSchema).optional(),
  complianceSummary: askPunttComplianceSummarySchema.optional(),
});
export type AskPunttProjectData = z.infer<typeof askPunttProjectDataSchema>;

export {
  askPunttChatAtomSchema,
  askPunttChatAtomIdSchema,
  ASK_PUNTT_CHAT_ATOM_IDS,
  ASK_PUNTT_NESTED_ONLY_CHAT_ATOM_IDS,
  type AskPunttChatAtom,
  type AskPunttChatAtomId,
  type AskPunttChatAtomOf,
} from "./chatAtoms";

/**
 * One check or reviewer leg of a running warhol turn, as warhol reported it.
 *
 * warhol publishes these to the AskPuntt streaming snapshot as each one settles
 * (`services/warhol/contracts/progress.py::ProgressEntry` →
 * `runtime/clients/ask_puntt_stream.py`), and carrie lays them onto the pending
 * row while the turn runs
 * (`ask-v2/transport/applyStreamSnapshot.ts`) so the rail can tick a row as its
 * check finishes instead of waiting for the whole fan-out to return. Nothing
 * writes them to the server: the row that carries them exists in the browser
 * only, for the length of the turn.
 *
 * `status` keeps warhol's own vocabulary rather than the rail's
 * (`pending`/`active`/`done`): a `failed` or `timed_out` leg must be able to
 * reach the rail as a failure, and collapsing four states into three is not a
 * decision this border gets to make.
 */
export const askPunttCheckProgressSchema = z.object({
  /** The roster name of a subagent, or a detcheck pass's own name. */
  name: z.string(),
  status: z.enum(["done", "failed", "timed_out", "skipped"]),
  /** Why it is not `done` — the failure, or a skipped check's own reason. */
  reason: z.string().optional(),
  /** When warhol says it settled, ISO-8601. */
  at: z.string(),
});
export type AskPunttCheckProgress = z.infer<typeof askPunttCheckProgressSchema>;

/**
 * The `w` warhol puts in front of a streaming snapshot's message id.
 *
 * It is the ADDRESS of a running turn's live snapshot, and three parties have
 * to spell it the same way: warhol writes the item under it
 * (`services/warhol/runtime/clients/ask_puntt_stream.py::STREAM_MESSAGE_PREFIX`,
 * the one copy that cannot share this constant), christo reads it back to write
 * the provisional review (`app/handlers/catalyst/warholChat.ts`), and carrie
 * subscribes to it (`ask-v2/transport/runningTurn.ts`). It is declared here and
 * only here because a literal drifting on one side is silent: the socket
 * subscription key is `chatId:messageId`, so a mismatched prefix subscribes to
 * an address nobody publishes to and simply never receives a frame.
 *
 * It also marks the row carrie's stream overlay synthesizes for a turn christo
 * has not written a row for, which is how that row is told apart from christo's
 * own `pending` placeholder, and it may carry no colon — the client splits a
 * subscription key on one.
 */
export const ASK_PUNTT_STREAM_MESSAGE_PREFIX = "w";

// Message
export const askPunttMessageSchema = z.object({
  _id: z.string(),
  chatId: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  status: z.enum(["pending", "completed"]).optional(),
  /** Assistant-only reason the agent answer degraded or failed. */
  error: z.string().optional(),
  structuredContent: askPunttStructuredContentSchema.optional(),
  /**
   * Assistant-only: Knowledge Database rule citations attached by the
   * `approved_claims_kb` tool when standard chat answers a claim question.
   * Same `ticketMessageCitation` union the review stores, so chat and review
   * render KDB-rule citations identically (see CommentCitationLinks).
   */
  citations: z.array(ticketMessageCitationSchema).optional(),
  activeSkill: z.string().optional(),
  canvasContent: askPunttCanvasContentSchema.optional(),
  searchProgress: z.array(askPunttSearchProgressSchema).optional(),
  agentSteps: z.array(askPunttAgentStepSchema).optional(),
  /**
   * Assistant-only streaming data: the checks a running warhol turn has settled
   * so far, newest last. Warhol publishes one per reviewer, detcheck pass and
   * verifier. The Progress rail reads its plan from `write_plan` in
   * `agentSteps`; the thread's live review still reads this off the overlaid
   * message. Nothing persists it, so it never arrives from chat history.
   */
  checkProgress: z.array(askPunttCheckProgressSchema).optional(),
  /**
   * Assistant-only: chat atoms the agent drew on this turn (warhol
   * `render_component`), camelCased at the christo border. A form here takes
   * its answer as the person's next message, in words -- no status rides back.
   */
  components: z.array(askPunttChatAtomSchema).optional(),
  contentBlocks: z.array(askPunttContentBlockSchema).nullable().optional(),
  attachments: z.array(askPunttAttachmentSchema).optional(),
  /**
   * Assistant-only: artwork version comparisons the turn made, window by
   * window (warhol `compare_artwork_versions`). The tall picture of the same
   * comparison is in `attachments`.
   */
  comparisons: z.array(askPunttVersionComparisonSchema).optional(),
  /** User-highlighted snippets sent with this turn (Ask V2 composer context). */
  quotedContexts: askPunttQuotedContextsSchema.optional(),
  /** User-selected review finding sent with this turn (Ask V2 composer context). */
  commentContexts: askPunttCommentContextsSchema.optional(),
  /** Open item from the project rail attached to this turn (Ask V2). */
  openItemContexts: askPunttOpenItemContextsSchema.optional(),
  viewTicketId: z.string().optional(),
  /** V2 pointer at AskPunttReview._id. */
  viewReviewId: z.string().optional(),
  /** Assistant-only structured private/public offer. */
  scopeOffer: askPunttScopeOfferSchema.optional(),
  paneLabels: z
    .object({
      context: z.string().optional(),
      artwork: z.string().optional(),
    })
    .optional(),
  suggestedResponses: z.array(z.string()).nullable().optional(),
  userQuestions: z.array(askPunttUserQuestionSchema).nullable().optional(),
  /** @deprecated Legacy chat-as-project data; superseded by AskPunttProject. */
  projectData: askPunttProjectDataSchema.nullable().optional(),
  streamingContent: z.string().nullable().optional(),
  canvasLoading: z.boolean().nullable().optional(),
  /** @deprecated Legacy chat-as-project streaming flag; see AskPunttProject. */
  projectLoading: z.boolean().nullable().optional(),
  canvasChangedSectionIds: z.array(z.string()).optional(),
  feedback: z.enum(["thumbs_up", "thumbs_down"]).nullable().optional(),
  /**
   * Display-only: when true the message is excluded from the rendered chat
   * timeline (e.g. the synthesized transcript an ingested shared chat is stored
   * as — the agent reads its content, but the user only wants the
   * "Shared the chat…" note plus the agent's outcome turn).
   */
  hidden: z.boolean().optional(),
  /** Present on user-role messages in multi-member project shared chats. */
  userId: z.string().optional(),
  /**
   * Actor ids mentioned in a user message: member `_id`s plus the `"puntt"`
   * sentinel for the AI agent. When the chat owner @-mentions someone, christo
   * also grants chat/project access before persisting the message.
   */
  mentions: z.array(z.string()).optional(),
  /** Email addresses @-mentioned in the composer (invite + access grant). */
  mentionEmails: z.array(z.string().email()).optional(),
  /**
   * Assistant-only: the `_id` of the user message this response answers.
   * Set once at placeholder creation for provider-triggered (Asana/Wrike)
   * turns; never inferred from message ordering. Unset for other turns.
   */
  inReplyToMessageId: z.string().optional(),
  /** Assistant-only final persistence time; createdAt is placeholder/start. */
  replyCompletedAt: z.string().optional(),
  /**
   * Assistant-only: true on the terminal message a review leaves behind when it
   * could not run at all (`_save_review_failed_message` in rembrandt). The rail
   * reads it to stop showing a review as in flight once the turn is over: the
   * "Start review" user message stays in the thread forever, and a failed turn
   * leaves no review to finish, so without this the rail shows "running" for
   * the rest of the chat's life. Absent on every other message.
   */
  reviewFailed: z.boolean().optional(),
  createdAt: z.string(),
});
export type AskPunttMessage = z.infer<typeof askPunttMessageSchema>;

export const reviewChecklistItemSchema = z.object({
  label: z.string(),
  sub: z.string(),
});
export type ReviewChecklistItem = z.infer<typeof reviewChecklistItemSchema>;

// Live per-finding evidence counter for the artwork_review_v2 progress bar:
// how many candidate findings have been verified out of the total seen so far.
export const reviewEvidenceSchema = z.object({
  verified: z.number(),
  total: z.number(),
});
export type ReviewEvidence = z.infer<typeof reviewEvidenceSchema>;

// Agent eval result — populated by
// services/rembrandt/scripts/askpuntt_agent_eval.py. One snapshot per chat,
// overwritten on each run. Mirrored in the Mongoose model at
// packages/commons-node/src/models/askPunttChat.ts so strict-mode Mongoose
// preserves the field on updates.
export const askPunttAgentEvalMetricSchema = z.object({
  score: z.number().nullable(),
  pass: z.boolean().nullable(),
  reason: z.string().nullable(),
});
export type AskPunttAgentEvalMetric = z.infer<
  typeof askPunttAgentEvalMetricSchema
>;

// One judged tool call from an exchange's agentSteps. Deterministic entries
// (error-convention fails, deferred-confirmation skips) never hit the LLM
// judge; skipped entries carry a skipReason and null scores.
export const askPunttAgentEvalToolCallSchema = z.object({
  toolName: z.string(),
  displayName: z.string().optional(),
  score: z.number().nullable(),
  pass: z.boolean().nullable(),
  reason: z.string().nullable(),
  skipped: z.boolean().default(false),
  skipReason: z.string().nullable().optional(),
  deterministic: z.boolean().default(false),
  // Durable source linkage/timing written by the evaluator. These fields are
  // metadata-only: no tool arguments, result content, or judge prose is copied
  // into immutable telemetry.
  sourceToolCallId: z.string().optional(),
  toolResultOrdinal: z.number().optional(),
  identityQuality: z.string().optional(),
  toolStartedAt: z.string().nullable().optional(),
  toolCompletedAt: z.string().nullable().optional(),
  timestampQuality: z.string().optional(),
  applicability: z.string().optional(),
  measurementStatus: z.string().optional(),
  reasonCode: z.string().nullable().optional(),
  deterministicRuleId: z.string().nullable().optional(),
});
export type AskPunttAgentEvalToolCall = z.infer<
  typeof askPunttAgentEvalToolCallSchema
>;

export const askPunttAgentEvalExchangeSchema = z.object({
  userInputPreview: z.string(),
  // Headline metric. Combines this user's actual request (primary signal)
  // with the active skill's promise (secondary frame) into a single score
  // for "did the AI do what the user wanted?"
  // Optional so legacy agentEval snapshots (pre-rename, containing
  // skillQuality but no goalAchievement) still parse cleanly during the
  // re-eval rollout; populated on every fresh run.
  goalAchievement: askPunttAgentEvalMetricSchema.nullable().optional(),
  // Legacy field name. Kept optional so historical snapshots written before
  // the GoalAchievement rename still parse. New runs do NOT populate this.
  skillQuality: askPunttAgentEvalMetricSchema.nullable().optional(),
  taskCompletion: askPunttAgentEvalMetricSchema.nullable(),
  stepEfficiency: askPunttAgentEvalMetricSchema.nullable(),
  // Populated only when the exchange's chat actually created tickets and we
  // can evaluate whether the agent assembled the right asset_context. Null
  // for chat-only exchanges (no ticket → no signal to judge).
  ticketContext: askPunttAgentEvalMetricSchema.nullable(),
  // Substantive-correctness axis (work turns only; the Python writer has
  // populated this since the GoalAchievement split — optional so snapshots
  // predating it still parse).
  responseCorrectness: askPunttAgentEvalMetricSchema.nullable().optional(),
  // Voice axis: does the reply sound like the live get_chat_voice() block?
  chatVoice: askPunttAgentEvalMetricSchema.nullable().optional(),
  // Input axis: were the attached files ready for the agent (described by the
  // pipeline, layout-analyzed when the skill needs geometry, carried onto a
  // prepared review board) and did the agent use them / degrade honestly?
  // Null on turns that involved no files.
  fileReadiness: askPunttAgentEvalMetricSchema.nullable().optional(),
  // Deterministic counts behind that score (no filenames — counts only), so a
  // "was this an input problem or a model problem" triage needs no re-run.
  fileReadinessFacts: z.record(z.unknown()).nullable().optional(),
  // Mean over the exchange's judged tool calls; per-call detail in toolCalls.
  toolSuccess: askPunttAgentEvalMetricSchema.nullable().optional(),
  toolCalls: z.array(askPunttAgentEvalToolCallSchema).optional(),
  // Work turns (agent had materials / produced a deliverable) are the
  // fair-to-judge cohort; input-gathering turns are bucketed out.
  isWorkTurn: z.boolean().optional(),
  ticketTitles: z.array(z.string()).default([]),
  // Live (non-deleted) review comments.
  reviewCommentCount: z.number().default(0),
  // All review comments including soft-deleted — denominator for dismissalRate.
  totalCommentCount: z.number().default(0),
  // 1 - (live / total). Closest organic proxy for a thumbs-down. Null when
  // there are no tickets or no comments — we don't dilute the average with
  // "no signal" cases.
  dismissalRate: z.number().nullable().optional(),
  // Deterministic failure bucket (infra terminal failure or agent error
  // reply) — scored goalAchievement=0 without a judge call.
  terminalFailureReason: z.string().nullable().optional(),
  error: z.string().nullable(),
});
export type AskPunttAgentEvalExchange = z.infer<
  typeof askPunttAgentEvalExchangeSchema
>;

// Per-comment eval result (pin placement + citation metrics) for one AI
// review comment on a ticket linked to this chat. Skip reasons are measured
// outcomes ("couldn't judge"), never zero scores.
export const askPunttAgentEvalCommentSchema = z.object({
  ticketId: z.string(),
  ticketTitle: z.string().nullable().optional(),
  commentId: z.string(),
  commentPreview: z.string().default(""),
  anchorType: z.enum(["pin", "cellAnchor", "video", "none"]),
  hasCitation: z.boolean().default(false),
  pinPlacement: askPunttAgentEvalMetricSchema.nullable().optional(),
  pinSkipReason: z.string().nullable().optional(),
  citationNeeded: askPunttAgentEvalMetricSchema.nullable().optional(),
  citationPin: askPunttAgentEvalMetricSchema.nullable().optional(),
  citationSkipReason: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type AskPunttAgentEvalComment = z.infer<
  typeof askPunttAgentEvalCommentSchema
>;

export const askPunttAgentEvalSchema = z.object({
  evaluatedAt: z.union([z.string(), z.date()]),
  runId: z.string().nullable().optional(),
  exchanges: z.array(askPunttAgentEvalExchangeSchema),
  // Present when the run evaluated ticket comments (absent under
  // --no-comment-evals and on legacy snapshots). Deduped per chat by
  // (ticketId, commentId).
  commentEvals: z.array(askPunttAgentEvalCommentSchema).optional(),
});
export type AskPunttAgentEval = z.infer<typeof askPunttAgentEvalSchema>;

// Compact summary of a user we've shared a chat with (or the chat's owner,
// when the requester is a shared user). Used by the share dialog and the
// "Shared with me" sidebar attribution.
export const askPunttChatUserSummarySchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  email: z.string().optional(),
  avatar: z.string().nullable().optional(),
  // Persisted placeholder avatar colors so this user's chosen colors render in
  // owner/member/share attribution. Absent for legacy users (FE falls back to
  // the deterministic default keyed on _id).
  avatarBackgroundColor: z.string().nullable().optional(),
  avatarTextColor: z.string().nullable().optional(),
});
export type AskPunttChatUserSummary = z.infer<
  typeof askPunttChatUserSummarySchema
>;

// Chat
export const askPunttChatSchema = z.object({
  _id: z.string(),
  title: z.string(),
  workflowType: askPunttWorkflowTypeSchema,
  enterpriseId: z.string(),
  userId: z.string(),
  enabledConnectors: z.array(z.string()).optional(),
  detectedSkill: z.string().nullable().optional(),
  ticketIds: z.array(z.string()).optional(),
  /** AskPunttReview ids created from this chat. */
  reviewIds: z.array(z.string()).optional(),
  reviewStatus: z.string().nullable().optional(),
  // Review engine this chat last ran with ("standard" -> v1, "deep" -> v2).
  // Persisted from the create/send `reviewMode` so the composer picker reflects
  // the chat, not the browser-wide UI preference. null on legacy/never-run chats.
  reviewMode: z.enum(["standard", "deep"]).nullable().optional(),
  // Which agent service answers this chat. Null/absent means rembrandt,
  // which is every chat created before warhol existed.
  agent: z.enum(["rembrandt", "warhol"]).nullable().optional(),
  reviewChecklist: z.array(reviewChecklistItemSchema).optional(),
  // Live artwork_review_v2 progress bar: ordered stage states (reusing the
  // project-stage shape) + a per-finding evidence counter. Written by the
  // rembrandt pipeline during a review, cleared/ignored otherwise.
  reviewStages: z.array(reviewStageSchema).optional(),
  reviewEvidence: reviewEvidenceSchema.optional(),
  // The scout/finding rows streamed while the "Review" stage runs — the same
  // writer, same lifetime, cleared with the fields above.
  reviewStream: z.array(reviewStreamRowSchema).optional(),
  // Non-fatal notes about what the finished review could NOT do — an outlined
  // pack whose OCR never landed, say, which silently disables every wording
  // check. Without these a text-blind run is indistinguishable from a clean one.
  reviewWarnings: z.array(z.string()).optional(),
  /** @deprecated Legacy chat-as-project data; superseded by AskPunttProject. */
  projectData: askPunttProjectDataSchema.nullable().optional(),
  // First-class project link. projectId == null ⇒ standalone chat (legacy
  // behavior). projectChatKind distinguishes the project's shared conversation
  // from members' private personal chats.
  projectId: z.string().nullable().optional(),
  projectChatKind: z.enum(["shared", "personal"]).nullable().optional(),
  /** Multi-value geography labels for org filters. */
  regions: z.array(z.string()).optional().default([]),
  /** Open-vocabulary org tags for search/filter. */
  tags: z.array(z.string()).optional().default([]),
  roiSession: askPunttRoiSessionSchema.optional(),
  agentEval: askPunttAgentEvalSchema.nullable().optional(),
  // Populated only when the requester has cross-user read access (MG admins
  // viewing other users' chats) OR is a shared user viewing the owner's
  // chat. Omitted for the chat owner's own chats.
  ownerName: z.string().optional(),
  ownerEmail: z.string().optional(),
  // Whether the requester is the owner ("owner") or accessed via the
  // allowList ("shared"). Used by the FE to gate the Share / Delete
  // affordances.
  role: z.enum(["owner", "shared"]).optional(),
  // Whether the requester may post into this chat (owner or allowList
  // member). False for MG cross-user readers and non-member viewers, whose
  // sends are rejected with 403 — the FE renders a read-only composer for
  // them. role alone cannot express this: a shared allowList user (may
  // post) and an MG reader (may not) both see role === "shared".
  canInteract: z.boolean().optional(),
  // Hydrated list of users this chat has been shared with. Populated by the
  // share dialog and chat-history responses. Order is insertion order.
  allowList: z.array(askPunttChatUserSummarySchema).optional(),
  // Microsoft Teams conversation metadata for bot responses
  teamsMetadata: z
    .object({
      conversationId: z.string(),
      channelId: z.string().optional(),
      serviceUrl: z.string(),
      tenantId: z.string().optional(),
    })
    .optional(),
  /** Denormalized latest notifiable message for unread badges on list endpoints. */
  lastMessageAt: z.string().optional(),
  lastMessageRole: z.enum(["user", "assistant"]).optional(),
  lastMessageUserId: z.string().nullable().optional(),
  lastMessageId: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AskPunttChat = z.infer<typeof askPunttChatSchema>;

// --- Request/Response Schemas ---

// List chats
export const askPunttChatScopeSchema = z.enum(["mine", "shared", "all"]);
export type AskPunttChatScope = z.infer<typeof askPunttChatScopeSchema>;

export const listAskPunttChatsRequestSchema = z.object({
  query: z.object({
    limit: z.coerce.number().optional().default(20),
    offset: z.coerce.number().optional().default(0),
    // mine = chats owned by the requester (default, current behavior).
    // shared = chats where the requester is in allowList.
    // all = union of mine + shared, with role: "owner" | "shared" per row.
    scope: askPunttChatScopeSchema.optional().default("mine"),
    /** Case-insensitive substring match on title and tags. */
    search: z.string().trim().min(1).max(200).optional(),
    /**
     * Filter to chats that include every listed user as owner or on
     * allowList (AND).
     */
    participantIds: z
      .union([z.string(), z.array(z.string()).max(50)])
      .optional()
      .transform((value) => {
        if (value == null) return undefined;
        const raw = Array.isArray(value) ? value : [value];
        const ids = Array.from(
          new Set(raw.map((id) => id.trim()).filter((id) => id.length > 0)),
        );
        return ids.length > 0 ? ids : undefined;
      }),
    regions: askPunttOrgRegionsQuerySchema,
    tags: askPunttOrgTagsQuerySchema,
    sortBy: askPunttListSortBySchema.optional().default("updatedAt"),
  }),
});
export const listAskPunttChatsResponseSchema = z.object({
  chats: z.array(
    askPunttChatSchema
      .pick({
        _id: true,
        title: true,
        workflowType: true,
        userId: true,
        enabledConnectors: true,
        projectData: true,
        projectId: true,
        projectChatKind: true,
        regions: true,
        tags: true,
        updatedAt: true,
        // Which agent answers in this chat. Absent means rembrandt; a list
        // row needs it so a client can tell a warhol chat apart without
        // opening it.
        agent: true,
      })
      // Populated for MG callers viewing other users' chats AND for shared
      // users viewing chats they were granted access to.
      .extend({
        ownerName: z.string().optional(),
        ownerEmail: z.string().optional(),
        createdAt: z.string().optional(),
        role: z.enum(["owner", "shared"]).optional(),
        sharedAt: z.string().optional(),
        /** Owner + hydrated allowList summaries for list cards. */
        members: z.array(askPunttChatUserSummarySchema).optional(),
        hasUnread: z.boolean().optional(),
      }),
  ),
  total: z.number().default(0),
});
export type ListAskPunttChatsResponse = z.infer<
  typeof listAskPunttChatsResponseSchema
>;

// Get chat history
export const getAskPunttChatHistoryRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
  }),
});
export const getAskPunttChatHistoryResponseSchema = z.object({
  chat: askPunttChatSchema,
  messages: z.array(askPunttMessageSchema),
  userSeen: z
    .object({
      lastReadAt: z.string().nullable(),
      unseenArtifactKeys: z.array(z.string()),
    })
    .optional(),
  /**
   * Warhol chats only: is the turn this chat is waiting on still running.
   *
   * The browser's poll used to stop for warhol at a fixed 30 minutes, and a
   * review that outran the clock froze mid-rail for a person who stayed. Write
   * this because the server knows and the clock does not.
   *
   * ABSENT is not `false`: it means this read could not ask (no session, or
   * Temporal unreachable), and a client that read that as "it is over" would
   * stop polling through a blip and strand the answer.
   */
  pendingTurn: z.boolean().optional(),
});
export type GetAskPunttChatHistoryResponse = z.infer<
  typeof getAskPunttChatHistoryResponseSchema
>;

// Get chat enterprise ID (for auto-switching)
export const getChatEnterpriseIdRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
  }),
});
export const getChatEnterpriseIdResponseSchema = z.object({
  enterpriseId: z.string(),
});
export type GetChatEnterpriseIdResponse = z.infer<
  typeof getChatEnterpriseIdResponseSchema
>;

const MAX_ASK_PUNTT_MENTION_RECIPIENTS = 50;

// Create chat
export const createAskPunttChatRequestSchema = z.object({
  body: z
    .object({
      message: z.string(),
      workflowType: askPunttWorkflowTypeSchema.optional().default("general"),
      attachments: z.array(askPunttAttachmentSchema).optional().default([]),
      quotedContexts: askPunttQuotedContextsSchema.optional(),
      commentContexts: askPunttCommentContextsSchema.optional(),
      openItemContexts: askPunttOpenItemContextsSchema.optional(),
      enabledConnectors: z.array(z.string()).optional().default([]),
      mentions: z
        .array(z.string())
        .max(MAX_ASK_PUNTT_MENTION_RECIPIENTS)
        .optional(),
      mentionEmails: z
        .array(z.string().email())
        .max(MAX_ASK_PUNTT_MENTION_RECIPIENTS)
        .optional(),
      // UI review-engine picker for the first message ("standard" -> v1,
      // "deep" -> v2). Consulted server-side only when v2 routing is enabled.
      reviewMode: z.enum(["standard", "deep"]).optional(),
      // Routes the chat to warhol instead of rembrandt. Omitted by every
      // existing caller, and omitted means rembrandt.
      agent: z.enum(["rembrandt", "warhol"]).optional(),
      // One-turn opt-in. Christo expands this boolean into Warhol's trusted,
      // unrestricted WebSearchScope; callers cannot choose provider policy.
      webSearch: z.boolean().optional().default(false),
      // Set by the home page when a workflow card is opened (no attachments yet):
      // the backend responds with the skill's canned greeting as the first
      // assistant message and does NOT start the LLM workflow.
      workflowGreeting: z.boolean().optional(),
      // Optional project link — the single create path also creates chats inside
      // a project. projectId attaches the new chat to a project; projectChatKind
      // marks it as the project's shared conversation or a personal chat.
      projectId: z.string().optional(),
      projectChatKind: z.enum(["shared", "personal"]).optional(),
    })
    .superRefine((body, ctx) => {
      const recipientCount =
        (body.mentions?.length ?? 0) + (body.mentionEmails?.length ?? 0);
      if (recipientCount > MAX_ASK_PUNTT_MENTION_RECIPIENTS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["mentions"],
          message: `Combined mentions and mentionEmails cannot exceed ${MAX_ASK_PUNTT_MENTION_RECIPIENTS}`,
        });
      }
    }),
});
export const createAskPunttChatResponseSchema = z.object({
  chat: askPunttChatSchema,
  userMessage: askPunttMessageSchema,
  assistantMessage: askPunttMessageSchema.optional(),
  workflowId: z.string().optional(),
});
export type CreateAskPunttChatResponse = z.infer<
  typeof createAskPunttChatResponseSchema
>;

const askPunttInviteSummarySchema = z.object({
  recipientUserIds: z.array(z.string()),
  recipientEmails: z.array(z.string()),
  recipientTiers: z.array(z.enum(["project", "enterprise", "new_email"])),
});

// Send message
export const sendAskPunttMessageRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
  }),
  body: z
    .object({
      message: z.string(),
      attachments: z.array(askPunttAttachmentSchema).optional().default([]),
      quotedContexts: askPunttQuotedContextsSchema.optional(),
      commentContexts: askPunttCommentContextsSchema.optional(),
      openItemContexts: askPunttOpenItemContextsSchema.optional(),
      sectionContext: z
        .object({ id: z.string(), title: z.string() })
        .optional(),
      mentions: z
        .array(z.string())
        .max(MAX_ASK_PUNTT_MENTION_RECIPIENTS)
        .optional(),
      mentionEmails: z
        .array(z.string().email())
        .max(MAX_ASK_PUNTT_MENTION_RECIPIENTS)
        .optional(),
      // UI review-engine picker ("standard" -> v1, "deep" -> v2). Consulted
      // server-side only when v2 production routing is enabled.
      reviewMode: z.enum(["standard", "deep"]).optional(),
      // Applies only to this message and resets when the composer submits it.
      webSearch: z.boolean().optional().default(false),
    })
    .superRefine((body, ctx) => {
      const recipientCount =
        (body.mentions?.length ?? 0) + (body.mentionEmails?.length ?? 0);
      if (recipientCount > MAX_ASK_PUNTT_MENTION_RECIPIENTS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["mentions"],
          message: `Combined mentions and mentionEmails cannot exceed ${MAX_ASK_PUNTT_MENTION_RECIPIENTS}`,
        });
      }
    }),
});
export const sendAskPunttMessageResponseSchema = z.object({
  userMessage: askPunttMessageSchema,
  // workflowId is omitted when the demo enterprise returns a scripted
  // canned response (no Temporal workflow is started); see
  // services/christo/app/handlers/catalyst/demoAskPunttResponses.ts.
  workflowId: z.string().optional(),
  assistantMessage: askPunttMessageSchema.optional(),
  inviteSummary: askPunttInviteSummarySchema.optional(),
});
export type SendAskPunttMessageResponse = z.infer<
  typeof sendAskPunttMessageResponseSchema
>;

// Submit feedback
export const submitAskPunttFeedbackRequestSchema = z.object({
  params: z.object({ chatId: z.string(), messageId: z.string() }),
  body: z.object({ feedback: z.enum(["thumbs_up", "thumbs_down"]) }),
});
export const submitAskPunttFeedbackResponseSchema = z.object({
  success: z.boolean(),
});

// Delete chat
export const deleteAskPunttChatRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
});
export const deleteAskPunttChatResponseSchema = z.object({
  success: z.boolean(),
});
export type DeleteAskPunttChatResponse = z.infer<
  typeof deleteAskPunttChatResponseSchema
>;

// Rename chat (update title) and/or org labels — owner-only for title;
// members with access may update labels. The title is trimmed, must be
// non-empty after trimming, and is capped so a runaway paste can't bloat the
// document or the sidebar/breadcrumb rendering.
export const MAX_ASK_PUNTT_CHAT_TITLE_LENGTH = 200;
export const updateAskPunttChatRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
  body: z
    .object({
      title: z
        .string()
        .trim()
        .min(1, "Title must not be empty")
        .max(
          MAX_ASK_PUNTT_CHAT_TITLE_LENGTH,
          `Title must be at most ${MAX_ASK_PUNTT_CHAT_TITLE_LENGTH} characters`,
        )
        .optional(),
      /** Full replace; `[]` clears. */
      regions: askPunttOrgRegionsSchema.optional(),
      /** Full replace; `[]` clears. */
      tags: askPunttOrgTagsSchema.optional(),
    })
    .refine((b) => Object.keys(b).length > 0, {
      message: "At least one field must be provided",
    }),
});
export const updateAskPunttChatResponseSchema = z.object({
  chat: askPunttChatSchema,
});
export type UpdateAskPunttChatResponse = z.infer<
  typeof updateAskPunttChatResponseSchema
>;

// Abort message (stop generating)
export const abortAskPunttMessageRequestSchema = z.object({
  params: z.object({ chatId: z.string(), messageId: z.string() }),
});
export const abortAskPunttMessageResponseSchema = z.object({
  message: askPunttMessageSchema,
});
export type AbortAskPunttMessageResponse = z.infer<
  typeof abortAskPunttMessageResponseSchema
>;

// Project action (override stage / complete project)
/**
 * @deprecated Legacy chat-as-project action (operates on chat.projectData).
 * Superseded by the first-class AskPunttProject entity. Retained because the
 * live carrie FE still calls /ask-puntt/chats/:chatId/project/action; removal
 * ships with the FE migration.
 */
export const updateAskPunttProjectActionRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
  body: z.object({
    action: z.enum(["override_stage", "complete_project"]),
    stageName: z.string().optional(),
  }),
});

// Share chat with one or more enterprise users (owner-only).
export const shareAskPunttChatRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
  body: z
    .object({
      userIds: z.array(z.string()).max(50).optional(),
      emails: z.array(z.string().email()).max(50).optional(),
    })
    .refine((b) => (b.userIds?.length ?? 0) + (b.emails?.length ?? 0) >= 1, {
      message: "At least one userId or email must be provided",
    }),
});
export const shareAskPunttChatResponseSchema = z.object({
  allowList: z.array(askPunttChatUserSummarySchema),
  inviteSummary: askPunttInviteSummarySchema.optional(),
});
export type ShareAskPunttChatResponse = z.infer<
  typeof shareAskPunttChatResponseSchema
>;

// Unshare chat (remove a user from allowList) (owner-only).
export const unshareAskPunttChatRequestSchema = z.object({
  params: z.object({ chatId: z.string(), userId: z.string() }),
});
export const unshareAskPunttChatResponseSchema = z.object({
  allowList: z.array(askPunttChatUserSummarySchema),
});
export type UnshareAskPunttChatResponse = z.infer<
  typeof unshareAskPunttChatResponseSchema
>;

// Search enterprise users by name/email (server-side regex). Used by the
// share dialog and the rembrandt ShareChatTool. Scoped to the requester's
// enterprise via JWT.
export const searchEnterpriseUsersRequestSchema = z.object({
  query: z.object({
    q: z.string().max(120),
    limit: z.coerce.number().int().min(1).max(25).optional().default(10),
  }),
});
export const searchEnterpriseUsersResponseSchema = z.object({
  users: z.array(askPunttChatUserSummarySchema),
});
export type SearchEnterpriseUsersResponse = z.infer<
  typeof searchEnterpriseUsersResponseSchema
>;

// Edit-last + regenerate (HITL). Replaces the last user message in place.
export const regenerateAskPunttMessageRequestSchema = z.object({
  params: z.object({ chatId: z.string(), messageId: z.string() }),
  body: z.object({
    content: z.string().min(1),
  }),
});
export const regenerateAskPunttMessageResponseSchema = z.object({
  userMessage: askPunttMessageSchema,
  assistantMessage: askPunttMessageSchema.optional(),
  workflowId: z.string().optional(),
});
export type RegenerateAskPunttMessageResponse = z.infer<
  typeof regenerateAskPunttMessageResponseSchema
>;

// Cancel or edit a userQuestion without sending a new turn.
export const patchAskPunttUserQuestionRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
    messageId: z.string(),
    questionId: z.string(),
  }),
  body: z.object({
    action: z.enum(["cancel", "edit"]),
    question: z.string().min(1).optional(),
  }),
});
export const patchAskPunttUserQuestionResponseSchema = z.object({
  message: askPunttMessageSchema,
});
export type PatchAskPunttUserQuestionResponse = z.infer<
  typeof patchAskPunttUserQuestionResponseSchema
>;

// Accept a structured scope offer: stay shared or open/reuse personal.
export const postAskPunttChatScopeRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
  body: z.object({
    choice: z.enum(["personal", "shared"]),
    reviewId: z.string().optional(),
  }),
});
export const postAskPunttChatScopeResponseSchema = z.object({
  chatId: z.string(),
  projectChatKind: z.enum(["personal", "shared"]),
  created: z.boolean(),
  reviewId: z.string().optional(),
});
export type PostAskPunttChatScopeResponse = z.infer<
  typeof postAskPunttChatScopeResponseSchema
>;
